import { NUMERAL_RUN, toNumeralRegexSource } from './numeral'
import { RegexTable } from './regex-table'

/** 数字以外の部分を、全角と半角の違いを吸収する正規表現に変える。メタ文字はエスケープする */
function toTextRegexSource(text: string): string {
  return text.replace(/[a-zA-Zａ-ｚＡ-Ｚ&＆.*+?^$()|\\{}[\]－ー-]/g, (c) => {
    return RegexTable[c] || c
  })
}

/**
 * 単語を正規表現の文字列に変換する
 *
 * 数字の並びは数として読み、同じ数の別の書き方にも当たるようにする。
 * 1文字ずつ置き換えるのでは `10` と `十` のように長さの違う書き方を対応づけられないため、
 * 数字の並びをひとまとまりで扱う。
 */
function toWordRegexSource(word: string): string {
  // NUMERAL_RUN は捕捉グループを1つ持つので、奇数番目が数字の並びになる
  return word
    .split(NUMERAL_RUN)
    .map((part, index) => {
      if (index % 2 === 0) {
        return toTextRegexSource(part)
      }
      return toNumeralRegexSource(part) ?? toTextRegexSource(part)
    })
    .join('')
}

/** 検索ワードを、空白で区切られた単語ごとの正規表現に変換する */
function toKeywordRegexps(keyword: string): RegExp[] {
  const trimmedKeyword = keyword.trim()
  if (trimmedKeyword.length === 0) {
    return []
  }

  return trimmedKeyword
    .split(/\s+/)
    .filter((s) => s.length > 0)
    .map((w) => new RegExp(toWordRegexSource(w), 'i'))
}

/**
 * 検索ワードが検索対象にマッチするかを判定する関数
 *
 * **Usage**
 *
 * ```ts
 * containsAllKeywords("hogefuga", "hoge")      // true
 * containsAllKeywords("Hoge", "hoge")          // true
 * containsAllKeywords("hogefuga", "hoge fuga") // true
 * containsAllKeywords("fuga", "hoge")          // false
 * containsAllKeywords("塩浜二丁目", "塩浜2丁目") // true
 * ```
 *
 * **Details**
 *
 * 指定された `target` 文字列内に `keyword` が存在するかどうかを大文字小文字を区別せずに検証する。
 * 検索ワード `keyword` は空白で区切られた複数の単語を含むことができ、`target` 内で
 * それらがすべて見つかる場合のみ `true` を返す。
 */
export function containsAllKeywords(target: string, keyword: string): boolean {
  return createKeywordMatcher(keyword)([target])
}

/**
 * 検索ワードから、検索対象にマッチするかを判定する関数を作る
 *
 * **Usage**
 *
 * ```ts
 * const matchesKeyword = createKeywordMatcher("セブン 塩浜2")
 * matchesKeyword(["セブン-イレブン塩浜店", "東京都江東区塩浜二丁目"]) // true
 * ```
 *
 * **Details**
 *
 * 単語ごとに `targets` のいずれか1つに含まれていればよいので、
 * 店舗名と住所のように別々の文字列をまたいで検索できる。
 *
 * 正規表現への変換は `createKeywordMatcher` を呼んだ1回だけ行う。
 * 多数の候補を絞り込むときは、絞り込みの前にこの関数を呼んで結果を使い回す。
 */
export function createKeywordMatcher(keyword: string): (targets: string[]) => boolean {
  const regexps = toKeywordRegexps(keyword)
  if (regexps.length === 0) {
    return () => false
  }

  return (targets) => regexps.every((regexp) => targets.some((t) => t.match(regexp) !== null))
}
